import type { Element } from '../core/constants.ts';
import { ELEMENT_COLOR } from './palette.ts';
import { SPELLS_BY_ID, type SpellKind } from '../core/spells.ts';

/**
 * What each spell looks like in flight, and what it does when it lands.
 *
 * Kept as data because the grammar is the point: element sets the colour, kind
 * sets the shape and the motion, and tier only turns the brightness up. A player
 * being shot at has to answer "what is that and can it kill me" in about a
 * quarter of a second, and they can only do that if the answer is always encoded
 * the same way.
 *
 * Tier deliberately does *not* change shape, count or behaviour. The original
 * fired two darts at once for Shock Dart II, which made aiming worse the better
 * you got; levelling should feel like the same spell with more behind it.
 */

/** Silhouette in flight. Objects tumble and leave debris; energy does neither. */
export type ProjectileShape =
  | 'orb' // energy, no geometry to speak of
  | 'shard' // faceted, ice and glass
  | 'mass' // irregular lump, fire and stone bolts
  | 'blade' // flat and spinning, Sharp Edges
  | 'mace' // hafted and heavy, end over end
  | 'stone' // rough rock
  | 'barb' // ugly, hooked, for the things that curse rather than wound
  | 'sprite'; // a drawing on a card that turns to face you

export type ImpactStyle =
  | 'spark' // energy, a bright short flash
  | 'burst' // fire and area spells, an expanding shell
  | 'shatter' // ice and glass, fragments
  | 'clatter' // objects, which fall and lie there a moment
  | 'implode'; // void, which pulls inward instead of out

export interface SpellLook {
  shape: ProjectileShape;
  /** Radius in metres at tier I. Tier adds a little, never a lot. */
  size: number;
  /** Turns per second about the axis the shape spins on. */
  spin: number;
  /**
   * Which axis it turns about, in its own space after it has been aimed.
   *
   * Travel is local Z, so the choice says what the object is doing in the air.
   * `y` is end over end, which is how a thrown axe goes and the reason it needs
   * saying: the haft lies across the direction of travel, and spinning about
   * that haft would read as a drill rather than a throw. `z` is a blade turning
   * flat, like a saw. `tumble` is the two-axis wobble that suits a lump of rock
   * or a ball of fire, where there is no right way up to preserve.
   */
  spinAxis: 'x' | 'y' | 'z' | 'tumble';
  /** How far the streak trails behind, in metres. Zero for none. */
  trail: number;
  /**
   * How wide the streak is, as a multiple of the projectile's own size.
   *
   * Was fixed at 1.6 for everything, which is the single reason Wounding's axe
   * was invisible: a trail wider than the object making it hides the object.
   * A streak should be *narrower* than its source. Above about 1.2 it stops
   * reading as a trail and starts reading as a comet.
   */
  trailWidth: number;
  /** How strongly the streak draws, 0..1. Additive, so this goes a long way. */
  trailOpacity: number;
  /**
   * How strongly the projectile's own body draws, 0..1. Procedural shapes only —
   * a model has its own materials and is not touched by this.
   *
   * Was fixed at 0.9 for everything, which is most of why the balls arrive as a
   * wall of light. Additive blending stacks, so a large shape at 0.9 with a halo
   * over it and a trail behind it is three bright layers on the same pixels.
   */
  bodyOpacity: number;
  /** Light thrown by the projectile, in candela at tier I. */
  glow: number;
  /**
   * A soft shell of light around the projectile, as a multiple of its size.
   * Zero for none.
   *
   * The difference between this and `glow` is worth being clear about, because
   * they are easy to confuse and do different jobs. `glow` is a *light*: it
   * comes out of the pooled point lights, it lands on walls and faces, and it
   * does nothing whatever to the projectile's own appearance. This is
   * *geometry*: an additive shell drawn around the thing itself, which is what
   * actually makes it legible at distance.
   *
   * It matters most for modelled projectiles. An authored axe is lit like any
   * other object, so in a dark cathedral at twenty metres it is a dark shape
   * against a dark wall — the procedural shapes never had this problem because
   * they are additive light to begin with.
   */
  halo: number;
  /**
   * How strongly the halo draws, 0..1.
   *
   * Separate from `halo` because size and intensity are different complaints. A
   * halo that is too big reads as a bubble; one that is too bright washes out
   * whatever it is meant to be revealing — which for a modelled projectile is
   * the entire point of having modelled it.
   */
  haloOpacity: number;
  impact: ImpactStyle;
  /** Leaves something on the floor for a moment. Objects only. */
  debris: boolean;
  /**
   * A drawing to use as the body, instead of geometry. `shape` must be `sprite`.
   *
   * The grammar so far has been element-times-kind, which reads well and means
   * Ice Dart and Shock Dart are the same shape in two colours. One drawing each
   * is the cheapest way to make forty-seven spells look like forty-seven spells,
   * and no amount of parameters on a sphere gets there.
   *
   * Drawn additively and camera-facing, so it is *light* rather than an object —
   * which is right for energy and wrong for anything with mass. The Wounding axe
   * stays a model precisely because it needs to look lit.
   */
  art?: string;

  /**
   * Colour for the trail, the light and the halo, overriding the element's.
   *
   * The grammar is element-first and stays that way: leave this unset and a
   * spell is the colour of what it is made of, which is what every spell but one
   * should be. It exists for the spell whose element and whose *identity*
   * disagree — Wounding is `physical`, and physical is a pale bone white chosen
   * so that Bleeding and Fracture read as wounds rather than as a school. An axe
   * with a bone-white trail is an axe you cannot see and cannot name.
   *
   * It never touches the model's own material, only the light around it. A
   * bronze axe stays bronze; what changes is what it is glowing with.
   */
  accent?: number;
  /**
   * Whether a model for this shape takes the element's colour.
   *
   * False for the things that are objects rather than energy. A thrown axe is
   * bronze, and tinting it the pale grey of `physical` would throw away the only
   * reason to have modelled it. Fire and void have no such object to respect:
   * whatever shape they take, they have to read as fire and void.
   *
   * Ignored entirely when there is no model — the procedural shapes have no
   * material of their own to keep.
   */
  tinted: boolean;
}

/**
 * The defaults the table in docs/design-history.md (§11, Spell effects) describes. Darts are small and fast, bolts
 * are chunkier, balls are slow and obviously deadly, debuffs are ugly.
 */
/*
 * The defaults, one row per kind.
 *
 * Read down the opacity columns rather than across: a projectile is drawn three
 * times over on the same pixels — body, halo, trail — and every one of them is
 * additive, so they sum rather than occlude. Nine tenths body plus a third of a
 * halo is not "mostly body", it is one and a quarter, and anything past 1 is
 * white however it was tinted. A ball is the worst case because it is large
 * enough that the sum covers a good part of the screen.
 *
 * So the balls come down hardest: the shape stays the size it was, and it is the
 * *intensity* that stops being a wall of light. Trails are the widest lever if
 * something still reads as too much — see `trailWidth`, which for a long time was
 * fixed at 1.6 and hid whatever it was trailing from.
 */
const BY_KIND: Record<string, SpellLook> = {
  projectile: { shape: 'orb', size: 0.13, spin: 2, trail: 2.2, trailWidth: 1.2, trailOpacity: 0.26, bodyOpacity: 0.8, glow: 2.5, halo: 0, haloOpacity: 0.3, impact: 'spark', debris: false, tinted: true, spinAxis: 'tumble' },
  ball: { shape: 'mass', size: 0.42, spin: 0.6, trail: 3.4, trailWidth: 1.1, trailOpacity: 0.2, bodyOpacity: 0.55, glow: 6, halo: 0, haloOpacity: 0.24, impact: 'burst', debris: false, tinted: true, spinAxis: 'tumble' },
  debuff: { shape: 'barb', size: 0.16, spin: 3.5, trail: 1.6, trailWidth: 1.2, trailOpacity: 0.24, bodyOpacity: 0.8, glow: 1.8, halo: 0, haloOpacity: 0.3, impact: 'spark', debris: false, tinted: true, spinAxis: 'tumble' },
};

/**
 * Element says what a spell is made of. Never how it moves.
 *
 * The split matters: kind owns size, spin and trail, because "balls are slow and
 * big" is a fact about balls and has to survive whatever they are made of. When
 * element was allowed to set motion too, a Fire Ball inherited fire's spin and
 * ended up turning at exactly the rate of the Burn it is meant to loom next to —
 * which is the one comparison the grammar exists to make obvious.
 */
type Appearance = Pick<SpellLook, 'shape' | 'glow' | 'impact' | 'debris'>;

const BY_ELEMENT: Partial<Record<Element, Partial<Appearance>>> = {
  fire: { shape: 'mass', impact: 'burst' },
  cold: { shape: 'shard', impact: 'shatter' },
  earth: { shape: 'stone', glow: 1.2, impact: 'clatter', debris: true },
  physical: { shape: 'blade', glow: 1.0, impact: 'clatter', debris: true },
  light: { shape: 'orb', glow: 4, impact: 'spark' },
  void: { shape: 'orb', glow: 1.6, impact: 'implode' },
  mana: { shape: 'orb', glow: 3.2, impact: 'spark' },
};

/**
 * How big a drawn spell is at first rank, in metres.
 *
 * The drawings are authored 225px square against the 360 the first one used, and
 * the card is sized to match — so a spell is the size the artist drew it rather
 * than a size argued over here. Small on purpose: `tierScale` grows it with
 * rank, and something that starts large has nowhere to grow to.
 */
const SPRITE = 0.34;

/**
 * How fast a drawing turns as it flies, in turns per second.
 *
 * A card that faces the camera and never rotates is a decal travelling sideways
 * — the eye reads it as a sticker on the screen rather than as an object in the
 * room. Spinning it about the view axis costs nothing and is the entire
 * difference between a picture moving and a thing flying.
 *
 * Slow enough to read as tumbling rather than as a wheel. Anything much past one
 * turn a second on a symmetrical drawing simply looks like flicker.
 */
const SPRITE_SPIN = 0.55;

/**
 * A spell that is a picture.
 *
 * The three arguments are everything that still varies: which drawing, what the
 * impact looks like, and how much light it throws on the room. Everything the
 * geometry grammar exists to decide — shape, spin, core, rim — the artist has
 * already decided.
 */
function drawn(
  art: string, impact: ImpactStyle, glow: number, spin = SPRITE_SPIN,
): Partial<SpellLook> {
  return {
    shape: 'sprite', art, size: SPRITE, spin, trail: 0, halo: 0,
    glow, bodyOpacity: 1, impact,
    // A drawing is never tinted: the artist has already chosen the colours, and
    // multiplying an element over the top would fight them for control of a
    // decision they have made better than a palette entry can.
    tinted: false,
  };
}

/**
 * The handful of spells with a silhouette of their own.
 *
 * Wounding is the odd one: a primary that behaves like a secondary, because a
 * Cleric is meant to be dangerous up close. It gets the weight to match — a
 * hafted mace turning end over end, slow enough to see and heavy enough to
 * respect, which is exactly the read that should make you back off a Cleric.
 */
const BY_SPELL: Record<string, Partial<SpellLook>> = {
  /*
   * The one spell in the game that throws an object rather than a light.
   *
   * The axe is bronze and stays bronze — `tinted: false` is what protects the
   * only reason to have modelled it. Everything here is about making that bronze
   * *findable*, which it was not: `physical` is a pale bone white, so the axe
   * flew inside a wide white streak brighter than itself and arrived as a blur
   * with no silhouette. Three things were wrong at once and all three are the
   * same mistake — the effects around the object were doing the work the object
   * should do.
   *
   * So: the trail is cut to a third of its width and shortened to a streak, the
   * accent takes it to the violet the original Magestorm threw, and the halo
   * gives the axe a light of its own rather than borrowing the trail's. The
   * light comes up with it, because a purple flicker crossing the nave is how
   * you notice one of these coming before you have identified it.
   */
  wounding: {
    shape: 'mace', size: 0.48, spin: 2.0,
    // Short and narrow. This is the fix: at 1.6 the streak was wider than the
    // axe, and a trail wider than its source is an occluder.
    trail: 0.7, trailWidth: 0.35, trailOpacity: 0.02,
    // Up from 0.8, and now violet rather than bone, so it throws a colour onto
    // the floor and the walls as it goes by.
    glow: 0.2,
    // The part that makes the model read at range. An authored object is *lit*
    // where the procedural shapes are light, so it needs this and they do not.
    halo: 0.24,
    accent: 0xb06aff,
    impact: 'clatter', debris: true, tinted: false,
    // End over end, the way an axe is thrown. The haft lies across the line of
    // flight, so turning about the haft would read as a drill.
    spinAxis: 'x',
  },
  // Three bolts of the same school, so the same violet. Left as only an accent:
  // the rest of Wounding — Bleeding, Fracture, Cramp, Paralysis — is still bone
  // white, which is a question of whether the school reads by colour or the
  // element does, and worth deciding by eye rather than here.
  /*
   * Three of the same axe, so it takes the same model.
   *
   * It used to inherit the default `orb`, which meant a Cleric's signature spell
   * threw three balls of light while the spell it is a triple of threw an axe.
   * A little smaller than the single, because three at once fills more of the
   * view than one and a full-sized triad reads as a wall.
   */
  wounding_triad: {
    shape: 'mace', size: 0.48, spin: 2.0, tinted: false, spinAxis: 'x',
    accent: 0xb06aff, halo: 0.24, glow: 2.0,
    trail: 0.55, trailWidth: 0.5, trailOpacity: 0.02,
    impact: 'clatter', debris: true,
  },
  // Flat, like a saw. A razor turning end over end would present its edge to the
  // air half the time and look like it was falling rather than thrown.
  /*
   * Big, slow and obviously heavy — the whole point of the spell is that you can
   * see it coming and choose not to be there. A Stone Bolt was a beam nobody
   * could dodge; this has to advertise itself.
   *
   * Drawn rather than built, and the one drawing in the game that is not
   * symmetrical: a fist has a right way up, so it turns slowly enough to read as
   * tumbling rather than spinning, and keeps a faint trail because unlike the
   * energy spells it is an object with weight rather than a light in the air.
   */
  stone_fist: {
    shape: 'sprite', art: 'stone_fist', size: 0.62, spin: 0.35,
    trail: 0.9, trailWidth: 0.8, trailOpacity: 0.18, halo: 0,
    glow: 0.6, bodyOpacity: 1, tinted: false, impact: 'shatter', debris: true,
  },
  shrapnel_ball: { spin: 2.4, trail: 2.2 },

  /*
   * The drawn spells.
   *
   * Trail and halo are off for all of them: a drawing already carries its own
   * falloff, and a streak behind it is two artists' worth of glow arguing. If
   * one wants a tail it should be drawn with one.
   *
   * `SPRITE` is a quarter of a metre because the drawings are authored at 225px
   * against the 360 the first one used — the same 62.5%, so a card is the size
   * the artist drew it rather than a size chosen here. Tier still scales it, so
   * a fifth-rank dart is a little larger than a first, which is the whole reason
   * to start them small.
   */
  shock_dart: drawn('shock_dart', 'spark', 5),
  ice_dart: drawn('ice_dart', 'shatter', 3.2),
  burn: drawn('burn', 'burst', 5.5),
  // A spiked ball, so it whirls rather than tumbles.
  sharp_edges: drawn('sharp_edges', 'shatter', 1.2, 1.4),
  void_spike: drawn('void_spike', 'implode', 1.6),
  manastrike: drawn('manastrike', 'spark', 3.4),
  shock_bolt: { shape: 'orb', size: 0.2, trail: 3.2, glow: 5.5 },

  void_bolt: { shape: 'shard', size: 0.22, glow: 1.8, impact: 'implode' },
  void_ball: { shape: 'orb', glow: 2.2, impact: 'implode' },

  bleeding: { shape: 'barb', size: 0.17, spin: 5 },
  dispell: { shape: 'orb', size: 0.15, spin: 0, trail: 2.4, glow: 3, impact: 'spark' },
};

const FALLBACK: SpellLook = BY_KIND.projectile;

/** The finished look for a spell, defaults through element through spell. */
export function lookFor(spellId: string): SpellLook {
  const spell = SPELLS_BY_ID[spellId];
  if (!spell) return FALLBACK;

  const base = BY_KIND[spell.kind as SpellKind] ?? FALLBACK;
  return {
    ...base,
    ...(BY_ELEMENT[spell.element] ?? {}),
    ...(BY_SPELL[spellId] ?? {}),
  };
}

/**
 * What the light around a spell is coloured — its accent if it has one, and the
 * colour of its element otherwise.
 *
 * One function so that the trail, the halo, the point light and the impact
 * flash cannot disagree, which is exactly the drift the palette module was
 * pulled out of `actors.ts` to prevent.
 */
export function accentFor(look: SpellLook, element: Element): number {
  return look.accent ?? ELEMENT_COLOR[element];
}

/**
 * How much a tier adds. Deliberately gentle — a fifth-tier dart should look like
 * the same spell with more behind it, not like a different one.
 */
export function tierScale(tier: number): number {
  return 1 + (Math.max(1, Math.min(5, tier)) - 1) * 0.09;
}

export function tierGlow(tier: number): number {
  return 1 + (Math.max(1, Math.min(5, tier)) - 1) * 0.28;
}
